import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { AlunoSelectorComponent } from '../aluno-selector/aluno-selector';
import { BoletimComponent } from '../boletim/boletim';
import { RelatorioGeralComponent } from '../relatorio-geral/relatorio-geral';
import { PortalBackendService } from '../services/portal-backend.service';
import { AlunoComAnosLetivos, BoletimCompleto, DisciplinaComAvaliacoes, AnosLetivos } from '../models/portal.models';
import { InfoDialogComponent } from '../../../core/info-dialog/info-dialog.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-desempenho-academico',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, AlunoSelectorComponent, BoletimComponent, RelatorioGeralComponent],
  templateUrl: './desempenho-academico.html',
  styleUrl: './desempenho-academico.scss'
})
export class DesempenhoAcademicoComponent implements OnInit {
  alunos: AlunoComAnosLetivos[] = [];
  alunoSelecionado: AlunoComAnosLetivos | null = null;
  anosLetivos: AnosLetivos[] = [];
  anoSelecionado: AnosLetivos | null = null;

  abaAtiva: 'boletim' | 'relatorio' = 'boletim';

  boletim: BoletimCompleto | null = null;
  relatorioGeral: DisciplinaComAvaliacoes[] | null = null;

  carregandoBoletim = false;
  carregandoRelatorio = false;

  constructor(
    public dialog: MatDialog,
    private backendService: PortalBackendService) {}

  ngOnInit() {
    this.carregarAlunos();
  }

  carregarAlunos() {
    this.backendService.getAlunosComAnosLetivos().subscribe(response => {
      const alunosBackend = (response.body ?? []) as AlunoComAnosLetivos[];
      this.alunos = this.agruparAlunosPorId(alunosBackend);

      if (this.alunos.length > 0) {
        this.selecionarAluno(this.alunos[0]);
      }
    });
  }

  selecionarAluno(aluno: AlunoComAnosLetivos) {
    this.alunoSelecionado = aluno;
    this.anosLetivos = this.ordenarAnosLetivos(aluno.anosLetivos ?? []);
    this.anoSelecionado = this.anosLetivos.length > 0 ? this.anosLetivos[0] : null;
    this.boletim = null;
    this.relatorioGeral = null;
    this.carregarDados();
  }

  onAnoChange() {
    this.boletim = null;
    this.relatorioGeral = null;
    this.carregarDados();
  }

  carregarDados() {
    if (!this.alunoSelecionado || !this.anoSelecionado) return;

    if (this.abaAtiva === 'boletim') {
      this.carregarBoletim();
    } else {
      this.carregarRelatorioGeral();
    }
  }

  carregarBoletim() {
    if (!this.alunoSelecionado || !this.anoSelecionado) return;


    if (!this.verificarTurmaExistente(this.anoSelecionado)) {
      return;
    }

    const turmaId: any = this.anoSelecionado.turmaId;

    this.carregandoBoletim = true;
    this.backendService.getBoletim(this.alunoSelecionado.id, this.anoSelecionado.id, turmaId).subscribe({
      next: (boletim) => {
        this.boletim = boletim.body;
        this.carregandoBoletim = false;
      },
      error: () => {
        this.boletim = null;
        this.carregandoBoletim = false;
      }
    });
  }

  carregarRelatorioGeral() {
    if (!this.alunoSelecionado || !this.anoSelecionado) return;

    this.carregandoRelatorio = true;
    this.backendService.getAvaliacoes(this.alunoSelecionado.id, this.anoSelecionado.id).subscribe({
      next: (relatorio) => {
        this.relatorioGeral = this.filtrarRelatorioPorAnoSelecionado(relatorio ?? []);
        this.carregandoRelatorio = false;
      },
      error: () => {
        this.relatorioGeral = [];
        this.carregandoRelatorio = false;
      }
    });
  }

  selecionarAba(aba: 'boletim' | 'relatorio') {
    this.abaAtiva = aba;
    this.carregarDados();
  }

  getDescricaoAnoTurma(ano: AnosLetivos | null | undefined): string {
    if (!ano) return '';

    const turma = ano.turmaDescricao || ano.serie || '';
    return turma ? `${ano.descricao} - ${turma}` : ano.descricao;
  }

  getTurmaAtual(): string {
    return this.anoSelecionado?.turmaDescricao || this.anoSelecionado?.serie || this.alunoSelecionado?.serie || '';
  }

  private agruparAlunosPorId(alunos: AlunoComAnosLetivos[]): AlunoComAnosLetivos[] {
    const alunosMap = new Map<number, AlunoComAnosLetivos>();

    for (const aluno of alunos ?? []) {
      const existente = alunosMap.get(aluno.id);
      const anosDoAluno = this.normalizarAnosLetivos(aluno);

      if (!existente) {
        alunosMap.set(aluno.id, {
          ...aluno,
          anosLetivos: this.ordenarAnosLetivos(anosDoAluno)
        });

        continue;
      }

      existente.anosLetivos = this.ordenarAnosLetivos([
        ...(existente.anosLetivos ?? []),
        ...anosDoAluno
      ]);

      const anoMaisRecente = existente.anosLetivos[0];

      if (anoMaisRecente?.serie) {
        existente.serie = anoMaisRecente.serie;
      }
    }

    return Array.from(alunosMap.values());
  }

  private normalizarAnosLetivos(aluno: AlunoComAnosLetivos): AnosLetivos[] {
    return (aluno.anosLetivos ?? []).map((ano) => ({
      ...ano,
      turmaDescricao: ano.turmaDescricao || ano.serie || aluno.serie,
      serie: ano.serie || ano.turmaDescricao || aluno.serie,
      matriculaId: ano.matriculaId || aluno.matriculaId
    }));
  }

  private ordenarAnosLetivos(anos: AnosLetivos[]): AnosLetivos[] {
    const anosMap = new Map<string, AnosLetivos>();

    for (const ano of anos ?? []) {
      const chave = `${ano.id}-${ano.turmaId}`;
      const existente = anosMap.get(chave);

      if (!existente) {
        anosMap.set(chave, { ...ano });
        continue;
      }
      

      anosMap.set(chave, {
        ...existente,
        ...ano,
        turmaDescricao: existente.turmaDescricao || ano.turmaDescricao,
        serie: existente.serie || ano.serie,
        matriculaId: existente.matriculaId || ano.matriculaId
      });
    }

    // Ordena por ano e caso a a diferenca de ano letivo seja 0, ordena depois por matrícula (do mais recente para o mais antigo)
    return Array.from(anosMap.values()).sort((a, b) => {

      const diferencaAno = this.obterOrdemAno(b) - this.obterOrdemAno(a);

      if(diferencaAno !== 0) {
        return diferencaAno;
      }

      return (b.matriculaId ?? 0) - (a.matriculaId ?? 0);
    });
  }

  private obterOrdemAno(ano: AnosLetivos): number {
    const match = ano?.descricao?.match(/\d{4}/);
    if (match) {
      return Number(match[0]);
    }

    return Number(ano?.id ?? 0);
  }

  private filtrarRelatorioPorAnoSelecionado(relatorio: DisciplinaComAvaliacoes[]): DisciplinaComAvaliacoes[] {
    const ano = this.extrairAnoNumerico(this.anoSelecionado?.descricao);

    if (!ano) {
      return relatorio;
    }

    return relatorio
      .map((disciplina) => ({
        ...disciplina,
        avaliacoes: (disciplina.avaliacoes ?? []).filter((avaliacao) => {
          const data = new Date(avaliacao.dataAplicacao);
          return !Number.isNaN(data.getTime()) ? data.getFullYear() === ano : true;
        })
      }))
      .filter((disciplina) => (disciplina.avaliacoes ?? []).length > 0);
  }

  private extrairAnoNumerico(texto?: string | null): number | null {
    const match = texto?.match(/\d{4}/);
    return match ? Number(match[0]) : null;
  }

  private verificarTurmaExistente(ano: any ): boolean {
    if(!ano.turmaId) {
      const dialogRef = this.dialog.open(InfoDialogComponent, {
        data: {
          titulo: "Erro",
          mensagem: "Não foi encontrado uma turma para o aluno no ano letivo selecionado."
        }
      });
      
      return false;
    }
    return true;
  }
}
